import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { CreditCard, Smartphone, Watch, Link2, ShieldCheck } from "lucide-react";
import { TapToPayGlyph, ttpLabel } from "./TapToPayButton";
import { getTTPCapability, isNativeApp, requestNativeTTPEducation } from "../../utils/tapToPay";

/* Apple's official in-app merchant education video for Tap to Pay on iPhone
   partners (approved asset — do not replace with custom Apple artwork). */
const APPLE_EDUCATION_VIDEO =
  "https://developer.apple.com/tap-to-pay/files/Tap-to-Pay-on-iPhone-In-App-Explainer-Video-For-Partners.mp4";

const Step = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-full bg-sky-500/15 flex items-center justify-center shrink-0">
      <Icon className="w-4.5 h-4.5 w-5 h-5 text-sky-500" />
    </div>
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{text}</p>
    </div>
  </div>
);

/** Merchant education (Apple requirements 4.1–4.7): how to accept contactless
 *  cards, Apple Pay and other digital wallets. Shown right after enabling
 *  Tap to Pay and always reachable from Settings. */
export default function TapToPayEducation({ open, onClose }) {
  const isIOS = getTTPCapability().platform === "ios";

  // Apple req 4.1: on iOS 18+ present Apple's native ProximityReaderDiscovery
  // education sheet on top; this dialog remains as the <iOS 18 / Android
  // fallback and supplemental reference.
  React.useEffect(() => {
    if (open && isIOS && isNativeApp()) {
      requestNativeTTPEducation().catch(() => {});
    }
  }, [open, isIOS]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="ttp-education-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TapToPayGlyph className="w-5 h-5 text-sky-500" /> How to accept payments with {ttpLabel()}
          </DialogTitle>
          <DialogDescription>
            No card reader needed — your phone is the terminal.
          </DialogDescription>
        </DialogHeader>

        {isIOS && (
          <video
            controls
            playsInline
            preload="none"
            className="w-full rounded-xl border border-border"
            src={APPLE_EDUCATION_VIDEO}
            data-testid="ttp-education-video"
          />
        )}

        <div className="space-y-4 py-1">
          <Step
            icon={CreditCard}
            title="Contactless cards"
            text={isIOS
              ? "Enter the amount, then ask the customer to hold their contactless card horizontally over the contactless symbol at the top of your iPhone. Keep it there until you see the checkmark."
              : "Enter the amount, then ask the customer to hold their contactless card flat against the back of your phone until the payment is confirmed."}
          />
          <Step
            icon={Smartphone}
            title="Apple Pay & digital wallets"
            text={isIOS
              ? "Customers can pay with Apple Pay, Google Wallet or other digital wallets — they hold their phone near the top of your iPhone after unlocking their wallet (double-click the side button on iPhone)."
              : "Customers can pay with Google Pay, Apple Pay or other digital wallets by holding their unlocked phone against the back of your device."}
          />
          <Step
            icon={Watch}
            title="Smartwatches too"
            text="Apple Watch and Wear OS watches work the same way — hold the watch face near the contactless symbol."
          />
          <Step
            icon={ShieldCheck}
            title="Accepted payment types & PIN"
            text="Visa, Mastercard, American Express and Discover contactless cards and wallets are accepted. Payments are processed securely by Stripe. If a card requires a PIN, the customer enters it on your screen — accessibility options are available on the PIN entry screen."
          />
          <Step
            icon={Link2}
            title="If a card can't be read"
            text="You can always fall back to a secure payment link (text or email) or manual card entry from the same payment screen."
          />
        </div>

        <Button onClick={onClose} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="ttp-education-done">
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}
